import {
  Button,
  colors,
  IMInputText,
  StapleTable,
  StapleTableBody,
  StapleTableCell,
  StapleTableHeader,
  StapleTableHeading,
  StapleTableRow,
  Tooltip,
} from '@insurancemenu/imenu-components-lib';
import { capitalize } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { ModalType } from '@insurancemenu/imenu-components-lib/dist/helpers/ModalsHelper';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useTriggerRenewalsPageStore } from 'store/pages/tools/trigger-renewals/pageStore';
import { Status } from 'types/emailService';
import CollapsiblePreviewSection from 'components/common/trigger-renewals/CollapsiblePreviewSection';
import {
  newContactTypeOptions,
  userTypeLabels,
} from 'store/pages/tools/trigger-renewals/modals/manageEmailsModalDelegate';
import { Delete, InfoButtonFilled } from '@insurancemenu/imenu-components-lib/dist/icons';
import { AlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { EnrollmentStage } from 'types/enrollmentService';
import { EMAIL_STATUS_ICON } from '../../TriggerRenewalsTable/TriggerRenewalsRow';
import {
  ActionModalStyled,
  EmailStatusLabelStyled,
  SubmitButtonStyled,
  EmailStatusStyled,
  IMFormStyled,
  TableSectionStyled,
  TableSectionTitleStyled,
  NotifyAllUsersButtonStyled,
  TableSectionHeaderStyled,
  TextContentWrapperStyled,
  ActionCellWrapperStyled,
  ErrorMessageStyled,
  SubmitButtonWrapperStyled,
  TypeIMSelectStyled,
  DeleteButtonStyled,
  InfoBlockStyled,
  DisabledReasonsListStyled,
  DisabledReasonsTooltipStyled,
  NoDataMessageStyled,
} from './styles';

interface DeleteButtonProps {
  isAdditional: boolean;
  onClick: () => void;
}

const DeleteButton: React.FC<DeleteButtonProps> = ({ isAdditional, onClick }) => {
  const button = (
    <DeleteButtonStyled
      type="icon"
      color="custom"
      disabled={!isAdditional}
      customColor={colors.red[600]}
      onClick={onClick}
    >
      <Delete width="2.4rem" height="2.4rem" />
    </DeleteButtonStyled>
  );

  if (isAdditional) {
    return button;
  }

  return (
    <Tooltip label="Primary contacts cannot be removed" alignment={AlignmentTypes.BOTTOM_END}>
      {button}
    </Tooltip>
  );
};

const DisabledReasonsTooltip: React.FC<{ disabledReasons: string[] }> = observer(({ disabledReasons }) => {
  const reasonsList = (
    <DisabledReasonsListStyled>
      {disabledReasons.map((reason) => (
        <li key={reason}>{reason} </li>
      ))}
    </DisabledReasonsListStyled>
  );

  return (
    <DisabledReasonsTooltipStyled label={reasonsList} alignment={AlignmentTypes.BOTTOM_START}>
      <InfoBlockStyled>
        <InfoButtonFilled width="2.4rem" height="2.4rem" color={colors.yellow[600]} />
        Notify actions are unavailable for <b>{disabledReasons.length}</b>{' '}
        {disabledReasons.length === 1 ? 'reason' : 'reasons'}
      </InfoBlockStyled>
    </DisabledReasonsTooltipStyled>
  );
});

const ManageEmailsModal: React.FC = observer(() => {
  const {
    renewalGroupActionDelegate: { manageEmailsModalDelegate, activeContext },
  } = useTriggerRenewalsPageStore();

  const {
    isVisible,
    isLoading,
    subtitle,
    emails,
    close,
    sendEmailToUser,
    notifyAllUsers,
    newContactForm,
    isEmailAlreadyListed,
    saveNewContact,
    removeContact,
  } = manageEmailsModalDelegate;

  const [showNewContactSection, setShowNewContactSection] = useState(false);
  const enrollmentDelegate = activeContext?.enrollmentDelegate;
  const areEmailsAdded = emails.length > 0;
  const showNewContactTable = areEmailsAdded || isLoading;

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    setShowNewContactSection(!areEmailsAdded);
  }, [isVisible, areEmailsAdded]);

  const isNotifyAllowed =
    enrollmentDelegate &&
    enrollmentDelegate.stage === EnrollmentStage.RENEWAL &&
    !enrollmentDelegate.areRatesMissing &&
    !enrollmentDelegate.areExhibitsMissing;

  const disabledReasons = [
    enrollmentDelegate?.stage !== EnrollmentStage.RENEWAL && 'Enrollment is not in the Renewal stage',
    enrollmentDelegate?.areRatesMissing && enrollmentDelegate.missingProductsOrRates?.message,
    enrollmentDelegate?.areExhibitsMissing && enrollmentDelegate.missingExhibitText,
  ].filter(Boolean) as string[];

  function onCollapseToggle(): void {
    setShowNewContactSection(!showNewContactSection);
  }

  function onClose(): void {
    setShowNewContactSection(false);
    close();
  }

  function onSubmit(): void {
    void saveNewContact();
    setShowNewContactSection(false);
  }

  return (
    <ActionModalStyled
      title="Manage Emails"
      subtitle={subtitle}
      modalType={ModalType.NO_ICON}
      show={isVisible}
      onClose={onClose}
      modalContent={
        <>
          {!showNewContactTable && (
            <NoDataMessageStyled>
              <InfoButtonFilled width="2.4rem" height="2.4rem" color={colors.yellow[600]} />
              No recipients added yet — add the first one using the form below
            </NoDataMessageStyled>
          )}
          <CollapsiblePreviewSection
            title="Add Recipient"
            subtitle="Add another contact to receive email notifications"
            show={showNewContactSection}
            onToggle={onCollapseToggle}
            toggleButtonText="Form"
          >
            <IMFormStyled formController={newContactForm} id="new-contact-form">
              <IMInputText label="First Name" name="firstName" />
              <IMInputText label="Last Name" name="lastName" />
              <TypeIMSelectStyled label="Type" name="userType" options={newContactTypeOptions} />
              <IMInputText label="Email" name="email" size="m" />
              <SubmitButtonWrapperStyled>
                <SubmitButtonStyled
                  onClick={onSubmit}
                  size="large"
                  color="custom"
                  customColor={colors.green[600]}
                  disabled={isEmailAlreadyListed || !newContactForm.isFormValid()}
                >
                  Add Recipient
                </SubmitButtonStyled>
                {isEmailAlreadyListed && <ErrorMessageStyled>This email is already listed</ErrorMessageStyled>}
              </SubmitButtonWrapperStyled>
            </IMFormStyled>
          </CollapsiblePreviewSection>

          {showNewContactTable && (
            <TableSectionStyled>
              <TableSectionHeaderStyled>
                <TextContentWrapperStyled>
                  <TableSectionTitleStyled>Email Recipients</TableSectionTitleStyled>
                  {disabledReasons.length > 0 && <DisabledReasonsTooltip disabledReasons={disabledReasons} />}
                </TextContentWrapperStyled>
                {emails.length > 1 && (
                  <NotifyAllUsersButtonStyled
                    size="medium"
                    type="outlined"
                    color="primary"
                    onClick={notifyAllUsers}
                    disabled={!isNotifyAllowed}
                  >
                    Notify All Listed Recipients
                  </NotifyAllUsersButtonStyled>
                )}
              </TableSectionHeaderStyled>

              <StapleTable loading={isLoading} totalElements={emails.length} minHeight="30rem" maxHeight="unset">
                <StapleTableHeader>
                  <StapleTableRow>
                    <StapleTableHeading width="7rem">Type</StapleTableHeading>
                    <StapleTableHeading width="12rem">Name</StapleTableHeading>
                    <StapleTableHeading width="16rem">Email Address</StapleTableHeading>
                    <StapleTableHeading width="11rem">Status</StapleTableHeading>
                    <StapleTableHeading width="9rem" />
                  </StapleTableRow>
                </StapleTableHeader>
                <StapleTableBody>
                  {emails.map(({ emailAddress, status, userType, userName, isAdditional }) => (
                    <StapleTableRow key={`${userType}_${emailAddress}`}>
                      <StapleTableCell width="7rem">{userTypeLabels[userType]}</StapleTableCell>
                      <StapleTableCell width="12rem">{userName}</StapleTableCell>
                      <StapleTableCell width="16rem">{emailAddress}</StapleTableCell>
                      <StapleTableCell width="11rem">
                        <EmailStatusStyled>
                          <EmailStatusLabelStyled $status={status}>
                            {capitalize(Status[status].replaceAll('_', ' ').toLowerCase())}
                          </EmailStatusLabelStyled>
                          {EMAIL_STATUS_ICON[status]}
                        </EmailStatusStyled>
                      </StapleTableCell>
                      <StapleTableCell width="9rem">
                        <ActionCellWrapperStyled>
                          <Button
                            type="text"
                            size="small"
                            color="primary"
                            uppercase
                            disabled={!isNotifyAllowed}
                            onClick={(): void => {
                              void sendEmailToUser(emailAddress, userType);
                            }}
                          >
                            Notify
                          </Button>
                          <DeleteButton
                            isAdditional={isAdditional}
                            onClick={(): void => {
                              void removeContact(emailAddress, userType);
                            }}
                          />
                        </ActionCellWrapperStyled>
                      </StapleTableCell>
                    </StapleTableRow>
                  ))}
                </StapleTableBody>
              </StapleTable>
            </TableSectionStyled>
          )}
        </>
      }
    />
  );
});

export default ManageEmailsModal;
